// app/javascript/Pages/Areas/EditArea.tsx

import React, { useState } from 'react';
import { useForm } from '@mantine/form';
import { usePage } from '@inertiajs/react';
import { TextInput, Checkbox, Button, Box, Select, MultiSelect, Text, Container, Group } from '@mantine/core';
import { useUser } from '../../src/context/UserContext';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import FormLayout from '../../components/FormLayout';
import { Notifications, showNotification } from '@mantine/notifications';
import ImageUploadModal from '../../components/ImageUploadModal';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

interface AreaData {
  id: number;
  name: string;
  slug: string;
  country_id: number;
  strip_area_suffix: boolean;
  skyline_image_url?: string | null;
  meta_image_url?: string | null;
  city_ids?: number[];
  artist_ids?: number[];
  organizer_ids?: number[];
  editor_ids?: number[];
}

interface Artist {
  id: number;
  name: string;
}
interface Organizer {
  id: number;
  name: string;
}
interface Country {
  id: number;
  name: string;
}
interface City {
  id: number;
  name: string;
}

interface EditAreaProps extends InertiaPageProps {
  area: AreaData;
  countries: Country[];
  cities: City[];
  errors?: string[];
  artists: Artist[];
  organizers: Organizer[];
  power_users?: Array<{ id: number; username: string }>;
  [key: string]: any;
}

function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '');
}

export default function EditArea() {
  const { area, countries, cities, errors, artists, organizers, power_users = [] } = usePage<EditAreaProps>().props;
  const [skylineFile, setSkylineFile] = useState<File | null>(null);
  const [skylinePreview, setSkylinePreview] = useState<string | null>(area.skyline_image_url || null);
  const [metaFile, setMetaFile] = useState<File | null>(null);
  const [metaPreview, setMetaPreview] = useState<string | null>(area.meta_image_url || null);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useUser();
  const isSuperAdmin = user?.roles.includes('SuperAdmin');

  console.log('Power Users:', power_users);

  const form = useForm({
    initialValues: {
      name: area.name,
      slug: area.slug,
      country_id: area.country_id.toString(),
      strip_area_suffix: area.strip_area_suffix,
      city_ids: (area.city_ids || []).map(String),
      artist_ids: (area.artist_ids || []).map(String),
      organizer_ids: (area.organizer_ids || []).map(String),
      editor_ids: (area.editor_ids || []).map(String),
    },
    validate: {
      name: (v) => (v ? null : 'Name is required'),
      country_id: (v) => (v ? null : 'Country is required'),
    },
  });

  const countryOptions = countries.map((c) => ({ value: String(c.id), label: c.name }));
  const cityOptions = cities.map((c) => ({ value: String(c.id), label: c.name }));
  const artistOptions = artists.map((a) => ({ value: String(a.id), label: a.name }));
  const organizerOptions = organizers.map((o) => ({ value: String(o.id), label: o.name }));

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.currentTarget.value;
    form.setFieldValue('name', newName);
    if (!area.slug) form.setFieldValue('slug', slugify(newName));
  };

  const handleSkylineChange = (file: File) => {
    setSkylineFile(file);
    setSkylinePreview(URL.createObjectURL(file));
  };
  const handleMetaChange = (file: File) => {
    setMetaFile(file);
    setMetaPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (vals: typeof form.values) => {
    setIsLoading(true);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

    const formData = new FormData();

    formData.append('area[name]', vals.name);
    formData.append('area[slug]', vals.slug);
    formData.append('area[country_id]', vals.country_id);
    formData.append('area[strip_area_suffix]', String(vals.strip_area_suffix));
    vals.city_ids.forEach((id) => formData.append('area[city_ids][]', id));
    vals.artist_ids.forEach((id) => formData.append('area[artist_ids][]', id));
    vals.organizer_ids.forEach((id) => formData.append('area[organizer_ids][]', id));

    // Add the power user IDs from the form
    if (isSuperAdmin && form.values.editor_ids?.length) {
      form.values.editor_ids.forEach((id) => formData.append('area[editor_ids][]', id));
    }

    if (skylineFile) formData.append('area[skyline_image]', skylineFile);
    if (metaFile) formData.append('area[meta_image]', metaFile);

    try {
      const res = await fetch(`/city/${area.slug}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json',
        },
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        showNotification({
          color: 'green',
          title: 'Success',
          message: 'Area updated!',
          icon: <IconCheck />,
          position: 'top-right',
        });
        setTimeout(() => {
          window.location.href = data.redirect_url || `/city/${vals.slug}`;
        }, 1000);
      } else {
        const payload = await res.json().catch(() => ({}) as any);
        showNotification({
          color: 'red',
          title: 'Error',
          message: payload.error || 'Update failed',
          icon: <IconAlertCircle />,
        });
        if (payload.errors) form.setErrors(payload.errors);
      }
    } catch (err) {
      console.error(err);
      showNotification({ color: 'red', title: 'Error', message: 'Unexpected error', icon: <IconAlertCircle /> });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <FormLayout title={`Edit Area: ${area.name}`}>
        <Notifications />
        <form onSubmit={form.onSubmit(handleSubmit)}>
          {/* Skyline */}
          <Box mb="md">
            <Text size="sm" fw={500}>
              Skyline Image
            </Text>
            <ImageUploadModal previewUrl={skylinePreview} onImageChange={handleSkylineChange} />
          </Box>

          {/* Meta Image */}
          <Box mb="md">
            <Text size="sm" fw={500}>
              Meta Image
            </Text>
            <ImageUploadModal previewUrl={metaPreview} onImageChange={handleMetaChange} />
          </Box>

          {/* Name & Slug */}
          <TextInput
            label="Name"
            value={form.values.name}
            onChange={handleNameChange}
            error={form.errors.name}
            required
            mb="md"
          />
          <TextInput
            label="Slug"
            value={form.values.slug}
            onChange={(e) => form.setFieldValue('slug', e.target.value)}
            mb="md"
          />

          {/* Country */}
          <Select
            label="Country"
            data={countryOptions}
            value={form.values.country_id}
            onChange={(val) => form.setFieldValue('country_id', val || '')}
            error={form.errors.country_id}
            required
            mb="md"
          />

          {/* Strip suffix */}
          <Checkbox
            label="Strip 'area' suffix"
            checked={form.values.strip_area_suffix}
            onChange={(e) => form.setFieldValue('strip_area_suffix', e.currentTarget.checked)}
            mb="md"
          />

          {/* Multi-selects */}
          <MultiSelect
            label="Cities"
            data={cityOptions}
            value={form.values.city_ids}
            onChange={(val) => form.setFieldValue('city_ids', val)}
            searchable
            clearable
            mb="md"
          />
          <MultiSelect
            label="Artists"
            data={artistOptions}
            value={form.values.artist_ids}
            onChange={(val) => form.setFieldValue('artist_ids', val)}
            searchable
            clearable
            mb="md"
          />
          <MultiSelect
            label="Organizers"
            data={organizerOptions}
            value={form.values.organizer_ids}
            onChange={(val) => form.setFieldValue('organizer_ids', val)}
            searchable
            clearable
            mb="md"
          />

          {isSuperAdmin && (
            <MultiSelect
              label="Power Users (Area Editors)"
              data={power_users.map((u) => ({
                value: u.id.toString(),
                label: u.username,
              }))}
              {...form.getInputProps('editor_ids')}
              mb="xl"
            />
          )}

          {errors && errors.length > 0 && (
            <Box mb="md" style={{ color: 'red' }}>
              {errors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </Box>
          )}

          <Group justify="right">
            <Button type="submit" size="lg" fullWidth loading={isLoading}>
              Update Area
            </Button>
          </Group>
        </form>
      </FormLayout>
    </Container>
  );
}
